import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Avatar, Button, Empty, Input, List, Radio, Skeleton, Space, Grid, message } from 'antd';
import {
  EnvironmentOutlined,
  ShopOutlined,
  CloseOutlined,
  TagsOutlined,
  RightOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import { getCurrentPosition } from '@core/platform';
import { useLocations } from '@core/hooks/useLocations';
import { useStoreContext } from '@core/hooks/useStoreContext';
import { getLocationBrandOrgSettings, getLocationOrgContext } from '@core/services/locationService';
import { searchLocations } from '@core/services/locationSearchService';
import { getActiveAccount, useAccountSelectionStore } from '@core/store/accountSelectionStore';
import { setRuntimeApiKey } from '@core/store/runtimeApiStore';
import { useUiStore } from '@core/store/uiStore';
import {
  applyAccountRuntime,
  applyOrgRuntime,
  getAccountIndustryTheme,
  getOrgIndustryTheme,
  pickApiKey,
} from '@core/utils/accountRuntime';
import { resolveIndustry } from '@config/industryRegistry';
import AppModal from '@components/base/AppModal';
import { useIndustry } from '@/app/providers/industry-context.js';

function haversineKm(aLat, aLng, bLat, bLng) {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const aa =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(aa), Math.sqrt(1 - aa));
  return R * c;
}

function storePoint(store) {
  const lat = Number(store.latitude);
  const lng = Number(store.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
}

const NEARBY_STORE_RADIUS_KM = 40;
const CART_DRAWER_SELECTOR = [
  '.grocery-cart-drawer',
  '.fish-cart-drawer',
  '.meat-cart-drawer',
  '.pet-cart-drawer',
].join(',');
const MOBILE_CART_BAR_SELECTOR = [
  '.default-mobile-cart-bar',
  '.restaurant-mobile-cart-bar',
  '.grocery-mobile-cart-bar',
  '.fish-mobile-cart-bar',
  '.meat-mobile-cart-bar',
  '.pet-mobile-cart-bar',
].join(',');

function brandKey(brand = {}) {
  return String(brand.org_brand_id || brand.brand_id || brand.id || '').trim();
}

function brandName(brand = {}) {
  return String(brand.display_name || brand.name || brand.brand_name || brandKey(brand)).trim();
}

function companyName(context = {}) {
  return String(
    context?.company_name || context?.company?.name || context?.companyName || '',
  ).trim();
}

function companyId(context = {}) {
  return String(context?.company_id || context?.company?.id || context?.companyId || '').trim();
}

function normalizeBrands(locationContext = {}) {
  const detailed = Array.isArray(locationContext?.brand_details)
    ? locationContext.brand_details
    : [];
  const simple = Array.isArray(locationContext?.brands) ? locationContext.brands : [];
  const byId = new Map();

  [...detailed, ...simple].forEach((brand) => {
    const id = brandKey(brand);
    if (!id) return;
    byId.set(id, { ...(byId.get(id) || {}), ...brand });
  });

  return Array.from(byId.values()).filter((brand) => brandName(brand));
}

function accountIndustryOrDefault(account) {
  return getAccountIndustryTheme(account) || resolveIndustry(window.location);
}

function waitForNextPaint() {
  if (typeof window === 'undefined') return Promise.resolve();
  return new Promise((resolve) => {
    window.requestAnimationFrame(() => window.requestAnimationFrame(resolve));
  });
}

export default function StoreSelectionProvider({ children }) {
  const queryClient = useQueryClient();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;
  const { setIndustry } = useIndustry();
  const { data: locations = [], isLoading: locationsLoading } = useLocations();
  const {
    accountId,
    locationId,
    brandId,
    brandName: selectedBrandName,
    companyName: selectedCompanyName,
    setLocation,
    setBrand,
    setRuntimeSwitching,
    customerLocation,
    setCustomerLocation,
    accountCountry,
  } = useStoreContext();
  const modalOpen = useUiStore((s) => s.storeSelectorOpen);
  const openStoreSelector = useUiStore((s) => s.openStoreSelector);
  const closeStoreSelector = useUiStore((s) => s.closeStoreSelector);
  const accountSelectorOpen = useAccountSelectionStore((s) => s.accountSelectorOpen);

  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [resolvingCurrentLocation, setResolvingCurrentLocation] = useState(false);
  const [options, setOptions] = useState([]);
  const [selectedPlace, setSelectedPlace] = useState(customerLocation || null);
  const [selectingStoreId, setSelectingStoreId] = useState('');
  const [pendingBrandLocation, setPendingBrandLocation] = useState(null);
  const [pendingBrandContext, setPendingBrandContext] = useState(null);
  const [brandOptions, setBrandOptions] = useState([]);
  const [selectingBrandId, setSelectingBrandId] = useState('');
  const [openingBrandSwitcher, setOpeningBrandSwitcher] = useState(false);
  const [cartDrawerOpen, setCartDrawerOpen] = useState(false);
  const [mobileCartBarOpen, setMobileCartBarOpen] = useState(false);
  const didClearStaleSelectionRef = useRef(false);

  const activeLocations = useMemo(() => {
    return locations.filter((l) => l?.isActive !== false && l?.isApproved !== false);
  }, [locations]);

  const hasValidSelectedStore = useMemo(() => {
    if (!locationId) return false;
    return activeLocations.some((loc) => loc.id === locationId);
  }, [activeLocations, locationId]);

  const activeAccountId = useAccountSelectionStore((s) => s.activeAccountId);
  const mustForceSelection =
    Boolean(activeAccountId) && !accountSelectorOpen && !locationsLoading && !hasValidSelectedStore;
  const isOpen = modalOpen || mustForceSelection;

  useEffect(() => {
    if (mustForceSelection) openStoreSelector();
  }, [mustForceSelection, openStoreSelector]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;

    const isVisibleElement = (element) => {
      const style = window.getComputedStyle(element);
      return (
        style.display !== 'none' &&
        style.visibility !== 'hidden' &&
        style.opacity !== '0' &&
        element.getAttribute('aria-hidden') !== 'true' &&
        !element.classList.contains('ant-drawer-hidden') &&
        (element.getClientRects().length > 0 ||
          element.classList.contains('ant-drawer-open') ||
          style.position === 'fixed')
      );
    };

    const updateCartDrawerState = () => {
      const drawers = Array.from(document.querySelectorAll(CART_DRAWER_SELECTOR));
      setCartDrawerOpen(
        drawers.some(
          (drawer) => drawer.classList.contains('ant-drawer-open') || isVisibleElement(drawer),
        ),
      );
      const mobileCartBars = Array.from(document.querySelectorAll(MOBILE_CART_BAR_SELECTOR));
      setMobileCartBarOpen(mobileCartBars.some(isVisibleElement));
    };
    const observer = new MutationObserver(updateCartDrawerState);

    updateCartDrawerState();
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style'],
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      setOptions([]);
    }
  }, [isOpen]);

  useEffect(() => {
    setQuery('');
    setOptions([]);
    setSelectedPlace(customerLocation || null);
    didClearStaleSelectionRef.current = false;
  }, [accountCountry, accountId, customerLocation]);

  useEffect(() => {
    setQuery('');
    setOptions([]);
    setSelectedPlace(null);
    didClearStaleSelectionRef.current = false;
  }, [accountId]);

  useEffect(() => {
    const trimmed = query.trim();
    if (!trimmed) {
      setOptions([]);
      return;
    }
    if (selectedPlace && trimmed === String(selectedPlace.label || '').trim()) {
      setOptions([]);
      return;
    }

    const id = window.setTimeout(async () => {
      setSearching(true);
      try {
        const list = await searchLocations({ query: trimmed, country: accountCountry });
        setOptions(list);
      } finally {
        setSearching(false);
      }
    }, 350);

    return () => window.clearTimeout(id);
  }, [accountCountry, query, selectedPlace]);

  const rankedStores = useMemo(() => {
    if (!selectedPlace) return [];

    return activeLocations
      .map((store) => {
        const point = storePoint(store);
        if (!point) return { ...store, distanceKm: null };
        return {
          ...store,
          distanceKm: haversineKm(selectedPlace.lat, selectedPlace.lng, point.lat, point.lng),
        };
      })
      .sort((a, b) => {
        if (a.distanceKm == null && b.distanceKm == null) return 0;
        if (a.distanceKm == null) return 1;
        if (b.distanceKm == null) return -1;
        return a.distanceKm - b.distanceKm;
      });
  }, [activeLocations, selectedPlace]);

  const nearbyStores = useMemo(() => {
    return rankedStores.filter(
      (store) => Number.isFinite(store.distanceKm) && store.distanceKm <= NEARBY_STORE_RADIUS_KM,
    );
  }, [rankedStores]);

  const showingFallbackAllStores = selectedPlace && nearbyStores.length === 0;
  const selectedPlaceLabel = String(selectedPlace?.label || '').trim();
  const isSelectedPlaceQuery = Boolean(selectedPlaceLabel && query.trim() === selectedPlaceLabel);
  const showingSearchFallbackAllStores = Boolean(
    !searching && query.trim() && !isSelectedPlaceQuery && options.length === 0,
  );
  const visibleStores = selectedPlace
    ? showingFallbackAllStores
      ? rankedStores
      : nearbyStores
    : showingSearchFallbackAllStores
      ? activeLocations
      : [];

  const onSelectPlace = (place) => {
    setSelectedPlace(place);
    setCustomerLocation({ name: place.name, lat: place.lat, lng: place.lng });
    setQuery(place.label);
    setOptions([]);
  };

  const beginRuntimeScopeSwitch = async () => {
    setRuntimeSwitching(true);
    await Promise.all([
      queryClient.cancelQueries({ queryKey: ['catalog'] }),
      queryClient.cancelQueries({ queryKey: ['cart'] }),
    ]);
    queryClient.removeQueries({ queryKey: ['catalog'], type: 'inactive' });
    queryClient.removeQueries({ queryKey: ['cart'], type: 'inactive' });
  };

  const refreshRuntimeQueries = () => {
    queryClient.removeQueries({ queryKey: ['catalog'], type: 'inactive' });
    queryClient.removeQueries({ queryKey: ['cart'], type: 'inactive' });
    queryClient.invalidateQueries({ queryKey: ['catalog'] });
    queryClient.invalidateQueries({ queryKey: ['cart'] });
    setRuntimeSwitching(false);
  };

  const applyBrandOrgContext = (context, extra = {}) => {
    const orgContext = { ...(context || {}), ...extra };
    applyOrgRuntime(orgContext);
    setIndustry(getOrgIndustryTheme(orgContext));
  };

  const applyCompanyOrgScope = (context = {}) => {
    const account = getActiveAccount();
    if (account) {
      applyAccountRuntime(account);
      setIndustry(accountIndustryOrDefault(account));
    }
    setRuntimeApiKey(pickApiKey(context) || pickApiKey(account));
  };

  const restoreAccountRuntime = async () => {
    const account = getActiveAccount();
    if (!account) return;
    await beginRuntimeScopeSwitch();
    setRuntimeApiKey(pickApiKey(account));
    applyAccountRuntime(account);
    setIndustry(accountIndustryOrDefault(account));
    setBrand('', '', '', '');
    await waitForNextPaint();
    refreshRuntimeQueries();
  };

  const selectStoreWithoutBrand = async (store, context = null) => {
    await beginRuntimeScopeSwitch();
    setBrand('', '', '', '');
    setLocation(store.id);
    if (context?.company_id) {
      applyCompanyOrgScope(context);
    }
    await waitForNextPaint();
    refreshRuntimeQueries();
    closeStoreSelector();
  };

  const selectSingleBrand = async (store, context, brand) => {
    const brandId = brandKey(brand);
    if (!brandId) {
      await selectStoreWithoutBrand(store, context);
      return;
    }

    const orgSettings = await getLocationBrandOrgSettings({
      locationId: store.id,
      brandId,
    });
    await beginRuntimeScopeSwitch();
    setBrand(
      brandId,
      brandName(brand),
      companyName(orgSettings) || companyName(context),
      companyId(orgSettings) || companyId(context),
    );
    setLocation(store.id);
    applyBrandOrgContext(orgSettings, { brand, brand_details: brand });
    await waitForNextPaint();
    refreshRuntimeQueries();
    closeStoreSelector();
  };

  const onSelectStore = async (store) => {
    if (!store?.id || selectingStoreId) return;

    setSelectingStoreId(store.id);
    try {
      const context = await getLocationOrgContext(store.id);
      const companyId = String(context?.company_id || '').trim();

      if (!companyId) {
        await restoreAccountRuntime();
        setLocation(store.id);
        await waitForNextPaint();
        refreshRuntimeQueries();
        closeStoreSelector();
        return;
      }

      const brands = normalizeBrands(context);
      if (brands.length === 0) {
        await selectStoreWithoutBrand(store, context);
        return;
      }

      if (brands.length === 1) {
        await selectSingleBrand(store, context, brands[0]);
        return;
      }

      setLocation(store.id);
      setBrand('', '', '', '');
      setPendingBrandLocation(store);
      setPendingBrandContext(context);
      setBrandOptions(brands);
      closeStoreSelector();
    } catch {
      setRuntimeSwitching(false);
      message.error('Unable to load company settings for this location.');
    } finally {
      setSelectingStoreId('');
    }
  };

  const onSelectBrand = async (brand) => {
    const brandId = brandKey(brand);
    if (!pendingBrandLocation?.id || !brandId || selectingBrandId) return;

    setSelectingBrandId(brandId);
    try {
      const orgSettings = await getLocationBrandOrgSettings({
        locationId: pendingBrandLocation.id,
        brandId,
      });
      await beginRuntimeScopeSwitch();
      setBrand(
        brandId,
        brandName(brand),
        companyName(orgSettings) || companyName(pendingBrandContext),
        companyId(orgSettings) || companyId(pendingBrandContext),
      );
      setLocation(pendingBrandLocation.id);
      applyBrandOrgContext(orgSettings, {
        brand,
        brand_details: brand,
      });
      await waitForNextPaint();
      refreshRuntimeQueries();
      setPendingBrandLocation(null);
      setPendingBrandContext(null);
      setBrandOptions([]);
    } catch {
      setRuntimeSwitching(false);
      message.error('Unable to load brand settings. Please try another brand.');
    } finally {
      setSelectingBrandId('');
    }
  };

  const onBackToStoreSelection = () => {
    setPendingBrandLocation(null);
    setPendingBrandContext(null);
    setBrandOptions([]);
    openStoreSelector();
  };

  const openBrandSwitcher = async () => {
    if (!locationId || openingBrandSwitcher) return;

    setOpeningBrandSwitcher(true);
    try {
      const context = await getLocationOrgContext(locationId);
      const brands = normalizeBrands(context);
      if (brands.length <= 1) {
        message.info('No other brands are available for this location.');
        return;
      }

      const activeLocation = activeLocations.find((location) => location.id === locationId) || {
        id: locationId,
        name: 'Selected location',
      };
      setPendingBrandLocation(activeLocation);
      setPendingBrandContext(context);
      setBrandOptions(brands);
      closeStoreSelector();
    } catch {
      message.error('Unable to load brands for this location.');
    } finally {
      setOpeningBrandSwitcher(false);
    }
  };

  const onUseCurrentLocation = () => {
    setResolvingCurrentLocation(true);
    getCurrentPosition({ enableHighAccuracy: true, timeout: 10000 })
      .then((position) => {
        const place = {
          name: 'Your current location',
          label: 'Your current location',
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        onSelectPlace(place);
        setResolvingCurrentLocation(false);
      })
      .catch(() => {
        setResolvingCurrentLocation(false);
      });
  };

  return (
    <>
      {children}
      {brandId && selectedBrandName ? (
        <button
          type="button"
          className={`current-brand-switcher ${
            cartDrawerOpen ? 'is-behind-drawer' : ''
          } ${isMobile && mobileCartBarOpen ? 'is-behind-mobile-cart' : ''}`}
          onClick={openBrandSwitcher}
          disabled={openingBrandSwitcher}
          aria-label={`Current brand: ${selectedBrandName}. Change brand`}
        >
          <TagsOutlined />
          <span>{selectedCompanyName || 'Brand'}</span>
          <strong>{selectedBrandName}</strong>
        </button>
      ) : null}
      <AppModal
        kind="store-select"
        className="store-selection-modal"
        open={isOpen}
        centered
        zIndex={10000}
        closeIcon={false}
        maskClosable={!mustForceSelection}
        keyboard={!mustForceSelection}
        onCancel={() => {
          if (!mustForceSelection) closeStoreSelector();
        }}
        footer={null}
        title={
          <div className="store-selection-header-title">
            <span>Select your location</span>
            {!mustForceSelection && (
              <CloseOutlined className="store-selection-close-icon" onClick={closeStoreSelector} />
            )}
          </div>
        }
        width={680}
        destroyOnHidden={false}
      >
        <Space direction="vertical" size={14} style={{ width: '100%' }}>
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search city or area (e.g., Chennai)"
            prefix={<EnvironmentOutlined />}
            allowClear
          />
          <Button
            icon={<EnvironmentOutlined />}
            onClick={onUseCurrentLocation}
            loading={resolvingCurrentLocation}
            disabled={!navigator.geolocation}
            block
          >
            {resolvingCurrentLocation ? 'Fetching current location...' : 'Use current location'}
          </Button>

          {searching ? <Skeleton active paragraph={{ rows: 3 }} /> : null}
          {!searching && options.length > 0 ? (
            <List
              bordered
              dataSource={options}
              renderItem={(item) => (
                <List.Item
                  className="store-selection-list-item"
                  onClick={() => onSelectPlace(item)}
                  style={{ cursor: 'pointer' }}
                >
                  <List.Item.Meta title={item.label} />
                </List.Item>
              )}
            />
          ) : null}

          {visibleStores.length > 0 ? (
            <List
              header={
                <strong>
                  {showingFallbackAllStores || showingSearchFallbackAllStores
                    ? 'All stores (no nearby store found)'
                    : 'Nearest stores *'}
                </strong>
              }
              bordered
              dataSource={visibleStores}
              renderItem={(store, index) => {
                const isSelected = store.id === locationId;
                return (
                  <List.Item
                    className={`store-selection-list-item ${isMobile ? 'has-radio' : ''}`}
                    onClick={isMobile ? () => onSelectStore(store) : undefined}
                    style={isMobile ? { cursor: 'pointer' } : undefined}
                    actions={[
                      isMobile ? (
                        <Radio
                          key={store.id}
                          checked={isSelected}
                          onChange={() => onSelectStore(store)}
                        />
                      ) : (
                        <Button
                          key={store.id}
                          type={isSelected ? 'default' : 'primary'}
                          loading={selectingStoreId === store.id}
                          onClick={() => onSelectStore(store)}
                        >
                          {isSelected ? 'Selected' : 'Select store'}
                        </Button>
                      ),
                    ]}
                  >
                    <List.Item.Meta
                      avatar={<ShopOutlined />}
                      title={`${index + 1}. ${store.name}`}
                      description={
                        !selectedPlace ||
                        store.distanceKm == null ||
                        store.distanceKm > NEARBY_STORE_RADIUS_KM
                          ? 'Distance unavailable'
                          : `${store.distanceKm.toFixed(1)} km away`
                      }
                    />
                  </List.Item>
                );
              }}
            />
          ) : null}

          {selectedPlace && activeLocations.length === 0 && !locationsLoading ? (
            <Empty
              description="No stores available near this location"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ) : null}
        </Space>
      </AppModal>
      <AppModal
        kind="brand-select"
        className="brand-selection-modal"
        open={Boolean(pendingBrandLocation && brandOptions.length > 1)}
        centered
        zIndex={10000}
        closable={false}
        maskClosable={false}
        keyboard={false}
        footer={null}
        title={
          <div className="brand-selection-header">
            <div className="brand-selection-header-main">
              <span className="brand-selection-header-icon">
                <ShopOutlined />
              </span>
              <div className="brand-selection-header-copy">
                <strong>Select Brand</strong>
                <span>Choose the brand you want to shop from</span>
              </div>
            </div>
            <Button
              className="brand-selection-change-store"
              type="text"
              onClick={onBackToStoreSelection}
            >
              Change store
            </Button>
            <button
              type="button"
              className="brand-selection-close"
              aria-label="Change store"
              onClick={onBackToStoreSelection}
            >
              <CloseOutlined />
            </button>
          </div>
        }
        width={490}
        destroyOnHidden={false}
      >
        <List
          className="brand-selection-list"
          dataSource={brandOptions}
          split={false}
          renderItem={(brand) => {
            const id = brandKey(brand);
            const title = brandName(brand);
            const isSelectedBrand = id === brandId;
            return (
              <List.Item
                className={`brand-selection-list-item ${isSelectedBrand ? 'is-selected' : ''}`}
                onClick={() => onSelectBrand(brand)}
                style={{ cursor: selectingBrandId ? 'default' : 'pointer' }}
                actions={[
                  <Button
                    key={id}
                    className="brand-selection-select-btn"
                    loading={selectingBrandId === id}
                    disabled={Boolean(selectingBrandId && selectingBrandId !== id)}
                    icon={isSelectedBrand ? <CheckOutlined /> : <RightOutlined />}
                    iconPosition="end"
                    onClick={(event) => {
                      event.stopPropagation();
                      onSelectBrand(brand);
                    }}
                  >
                    {isSelectedBrand ? 'Selected' : 'Select brand'}
                  </Button>,
                ]}
              >
                <List.Item.Meta
                  avatar={
                    <Avatar
                      className="brand-selection-avatar"
                      src={brand.logo?.url || brand.logo_url || undefined}
                      icon={!brand.logo?.url && !brand.logo_url ? <TagsOutlined /> : undefined}
                    />
                  }
                  title={<span className="brand-selection-name">{title}</span>}
                  description={
                    <span className="brand-selection-code">
                      {brand.code || pendingBrandLocation?.name || ''}
                    </span>
                  }
                />
              </List.Item>
            );
          }}
        />
      </AppModal>
    </>
  );
}
